import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

interface ReworkConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reworkNote: string) => void;
  onCancel: () => void;
}

export function ReworkConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel
}: ReworkConfirmDialogProps) {
  const [reworkNote, setReworkNote] = useState("");
  const { toast } = useToast();

  const handleConfirm = () => {
    if (!reworkNote.trim()) {
      toast({
        title: "Rework note required",
        description: "Please explain what needs to be fixed.",
        variant: "destructive",
      });
      return;
    }
    onConfirm(reworkNote);
    setReworkNote("");
  };

  const handleCancel = () => {
    onCancel();
    setReworkNote("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReworkNote("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Request Rework?</DialogTitle>
          <DialogDescription>
            This issue was awaiting your verification. Do you need changes?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="rework-note">What needs to be fixed?</Label>
            <Textarea
              id="rework-note"
              placeholder="Describe what needs rework... e.g., 'UI still shows incorrect layout on mobile devices'"
              value={reworkNote}
              onChange={(e) => setReworkNote(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Explain what still needs fixing so the developer knows what to address.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Just Change Status
          </Button>
          <Button onClick={handleConfirm} disabled={!reworkNote.trim()}>
            Request Rework
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
